import React, { useState } from 'react';
import { LightbulbIcon, CheckIcon, ClipboardIcon, ChevronDownIcon, ChevronRightIcon } from '@bizfirst/common-react';
import { ExecutionObserverConfig } from '../../models/ExecutionObserverConfig';
import '../../styles/tabs/ConsoleTab.css';

interface ConsoleLog {
  id: string;
  timestamp: Date;
  level: 'info' | 'debug' | 'warn' | 'error';
  message: string;
  data: any;
  formattedData: string;
  rawData: string;
  hierarchy?: 'process' | 'workflow' | 'node'; // Visual indentation level
}

interface ConsoleTabProps {
  config: ExecutionObserverConfig;
  logs?: ConsoleLog[];
  readonly?: boolean;
}

/**
 * Console Tab - Display observer logs with formatted and raw data views
 * Similar to Execution Logs with copy functionality
 */
export const ConsoleTab: React.FC<ConsoleTabProps> = ({
  config,
  logs = [],
  readonly = false,
}) => {
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('formatted');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedLogs(newExpanded);
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyAll = (format: 'formatted' | 'raw') => {
    const allText = logs
      .map((log) => {
        const header = `[${log.timestamp.toLocaleTimeString()}] [${log.level.toUpperCase()}] ${log.message}`;
        const data = format === 'formatted' ? log.formattedData : log.rawData;
        return `${header}\n${data}`;
      })
      .join('\n\n---\n\n');

    navigator.clipboard.writeText(allText);
    setCopiedId(`copy-all-${format}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return '#cc0000';
      case 'warn':
        return '#ff9900';
      case 'debug':
        return '#0066cc';
      default:
        return '#666666';
    }
  };

  return (
    <div className="console-tab">
      <div className="console-controls">
        <div className="view-mode-toggle">
          <label>
            <input
              type="radio"
              name="viewMode"
              value="formatted"
              checked={viewMode === 'formatted'}
              onChange={() => setViewMode('formatted')}
              disabled={readonly}
            />
            <span>Formatted</span>
          </label>
          <label>
            <input
              type="radio"
              name="viewMode"
              value="raw"
              checked={viewMode === 'raw'}
              onChange={() => setViewMode('raw')}
              disabled={readonly}
            />
            <span>Raw Data</span>
          </label>
        </div>

        <div className="console-stats">
          <span className="stat">Total: {logs.length}</span>
          <span className="stat error">Errors: {logs.filter((l) => l.level === 'error').length}</span>
          <span className="stat warn">Warnings: {logs.filter((l) => l.level === 'warn').length}</span>
        </div>

        <div className="copy-all-buttons">
          <button
            className="copy-all-btn"
            onClick={() => handleCopyAll('formatted')}
            title="Copy all logs in formatted view"
            disabled={logs.length === 0 || readonly}
          >
            {copiedId === 'copy-all-formatted' ? <><CheckIcon size={12} /> Copied</> : 'Copy All (Formatted)'}
          </button>
          <button
            className="copy-all-btn"
            onClick={() => handleCopyAll('raw')}
            title="Copy all logs in raw view"
            disabled={logs.length === 0 || readonly}
          >
            {copiedId === 'copy-all-raw' ? <><CheckIcon size={12} /> Copied</> : 'Copy All (Raw)'}
          </button>
        </div>
      </div>

      <div className="console-logs">
        {logs.length === 0 ? (
          <div className="console-empty">
            <p>No logs yet. Logs will appear here when observer events are triggered.</p>
          </div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              className={`console-log-item hierarchy-${log.hierarchy || 'process'}`}
              style={{
                paddingLeft: log.hierarchy === 'workflow'
                  ? 'var(--spacing-lg)'
                  : log.hierarchy === 'node'
                  ? 'var(--spacing-xl)'
                  : 0
              }}
            >
              <div
                className="console-log-header"
                onClick={() => toggleExpanded(log.id)}
              >
                {log.hierarchy && (
                  <span className="hierarchy-indicator">
                    {log.hierarchy === 'workflow' && '└─ '}
                    {log.hierarchy === 'node' && '  └─ '}
                  </span>
                )}
                <span
                  className="log-level"
                  style={{ color: getLevelColor(log.level) }}
                >
                  [{log.level.toUpperCase()}]
                </span>
                <span className="log-time">
                  {log.timestamp.toLocaleTimeString()}
                </span>
                <span className="log-message">{log.message}</span>
                <span className="log-expand">
                  {expandedLogs.has(log.id) ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}
                </span>
                <button
                  className="copy-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(log.formattedData, `formatted-${log.id}`);
                  }}
                  title="Copy formatted data"
                >
                  {copiedId === `formatted-${log.id}` ? <CheckIcon size={12} /> : <ClipboardIcon size={12} />}
                </button>
              </div>

              {expandedLogs.has(log.id) && (
                <div className="console-log-content">
                  <div className="log-data-container">
                    {viewMode === 'formatted' ? (
                      <>
                        <pre className="log-formatted">
                          {log.formattedData}
                        </pre>
                        <button
                          className="copy-btn"
                          onClick={() =>
                            handleCopy(log.formattedData, `formatted-${log.id}`)
                          }
                          title="Copy formatted data"
                        >
                          {copiedId === `formatted-${log.id}` ? <><CheckIcon size={12} /> Copied</> : 'Copy Formatted'}
                        </button>
                      </>
                    ) : (
                      <>
                        <pre className="log-raw">
                          {log.rawData}
                        </pre>
                        <button
                          className="copy-btn"
                          onClick={() =>
                            handleCopy(log.rawData, `raw-${log.id}`)
                          }
                          title="Copy raw data"
                        >
                          {copiedId === `raw-${log.id}` ? <><CheckIcon size={12} /> Copied</> : 'Copy Raw'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="console-info">
        <p>
          <LightbulbIcon size={14} /> <strong>Tip:</strong> Click on any log entry to expand/collapse.
          Use the toggle above to switch between formatted and raw data views.
        </p>
      </div>
    </div>
  );
};

export default ConsoleTab;

/**
 * Utility function to create a console log entry
 */
export function createConsoleLog(
  level: 'info' | 'debug' | 'warn' | 'error',
  message: string,
  data: any,
  hierarchy?: 'process' | 'workflow' | 'node'
): ConsoleLog {
  const formattedData = JSON.stringify(data, null, 2);
  const rawData = JSON.stringify(data);

  return {
    id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    level,
    message,
    data,
    formattedData,
    rawData,
    hierarchy,
  };
}
